<template>
  <b-card
    no-body
    class="rci-card hf-panel"
    :class="embedded ? 'hf-panel--embedded mb-0 mt-0 h-100' : 'mt-2 mb-2'"
  >
    <b-card-header class="rci-card-header">
      <div
        class="d-flex align-items-center justify-content-between w-100 flex-wrap"
      >
        <div class="d-flex align-items-center mb-50 mb-md-0">
          <div class="rci-header-icon mr-1">
            <span class="rci-header-icon__ring"></span>
            <feather-icon icon="PauseCircleIcon" size="22" />
          </div>
          <div>
            <div class="d-flex align-items-center flex-wrap">
              <h4 class="mb-0 rci-title">{{ $t("Fees on hold") }}</h4>
              <span
                v-if="stats.allCampuses"
                class="rci-status-pill rci-status-pill--demo ml-50"
              >
                {{ $t("All campuses") }}
              </span>
              <span v-else class="rci-status-pill rci-status-pill--live ml-50">
                <feather-icon icon="ActivityIcon" size="11" class="mr-25" />
                {{ $t("Live") }}
              </span>
            </div>
            <small class="rci-subtitle">
              <feather-icon icon="CalendarIcon" size="12" class="mr-25" />
              {{ $t("Held fee records by hold date and campus") }}
            </small>
          </div>
        </div>
        <div class="rci-header-meta">
          <span v-if="displayRecords.length && !embedded" class="rci-meta-chip">
            <feather-icon icon="ListIcon" size="12" class="mr-25" />
            {{ displayRecords.length }} {{ $t("records") }}
          </span>
          <span
            v-if="canOpenChallan && !embedded"
            class="rci-meta-chip rci-meta-chip--mode"
          >
            <feather-icon icon="ExternalLinkIcon" size="12" class="mr-25" />
            {{ $t("Click student to open challan in new tab") }}
          </span>
        </div>
      </div>
    </b-card-header>

    <b-card-body class="rci-body">
      <div class="rci-filter-bar mb-2">
        <div class="rci-filter-bar__head">
          <div class="rci-filter-bar__title">
            <feather-icon icon="FilterIcon" size="14" class="mr-50" />
            {{ $t("Date filters") }}
          </div>
          <small class="rci-filter-bar__hint">
            {{ $t("Uses hold till, else modification time") }}
          </small>
        </div>

        <div class="rci-filter-bar__grid hf-filter-actions">
          <b-dropdown
            variant="outline-primary"
            size="sm"
            class="hf-period-dd"
            toggle-class="hf-period-dd__toggle"
            :disabled="loading"
          >
            <template #button-content>
              <feather-icon icon="ClockIcon" size="14" class="mr-50" />
              {{ periodLabel }}
            </template>
            <b-dropdown-item
              v-for="opt in periodOptions"
              :key="opt.value"
              :active="datePeriod === opt.value"
              @click="applyPeriod(opt.value)"
            >
              {{ $t(opt.label) }}
            </b-dropdown-item>
          </b-dropdown>

          <div class="hf-date-control">
            <span class="hf-date-control__icon">
              <feather-icon icon="CalendarIcon" size="15" />
            </span>
            <div
              class="hf-date-control__field"
              role="button"
              tabindex="0"
              @click="openPicker('from')"
              @keydown.enter.prevent="openPicker('from')"
            >
              <span class="hf-date-control__label">{{ $t("From") }}</span>
              <flat-pickr
                ref="pickerFrom"
                v-model="dtFrom"
                :config="datePickrConfig"
                class="hf-date-control__input"
                :placeholder="$t('Start date')"
              />
            </div>
            <span class="hf-date-control__sep">→</span>
            <div
              class="hf-date-control__field"
              role="button"
              tabindex="0"
              @click="openPicker('to')"
              @keydown.enter.prevent="openPicker('to')"
            >
              <span class="hf-date-control__label">{{ $t("To") }}</span>
              <flat-pickr
                ref="pickerTo"
                v-model="dtTo"
                :config="datePickrConfig"
                class="hf-date-control__input"
                :placeholder="$t('End date')"
              />
            </div>
            <button
              v-if="dtFrom || dtTo"
              type="button"
              class="hf-date-control__btn"
              v-b-tooltip.hover.top
              :title="$t('Clear dates')"
              @click="clearDates"
            >
              <feather-icon icon="XIcon" size="14" />
            </button>
            <span class="hf-date-control__divider" />
            <button
              type="button"
              class="hf-date-control__btn hf-date-control__btn--primary"
              v-b-tooltip.hover.top
              :title="$t('Refresh')"
              :disabled="loading"
              @click="loadData"
            >
              <b-spinner v-if="loading" small variant="primary" />
              <feather-icon v-else icon="RefreshCwIcon" size="15" />
            </button>
          </div>
        </div>
      </div>

      <div v-if="loading" class="rci-skeleton py-2">
        <b-row class="mb-2">
          <b-col
            v-for="i in 3"
            :key="'hf-kpi-sk-' + i"
            md="4"
            cols="12"
            class="mb-1"
          >
            <div class="rci-skel-block" style="height: 92px"></div>
          </b-col>
        </b-row>
        <div class="rci-skel-block" style="height: 320px"></div>
      </div>

      <div v-else-if="loadError" class="rci-state rci-state--error">
        <div class="rci-state__icon">
          <feather-icon icon="AlertCircleIcon" size="28" />
        </div>
        <h5 class="rci-state__title">{{ $t("Could not load hold fee stats") }}</h5>
        <p class="rci-state__sub">{{ $t("Check your connection and try again.") }}</p>
        <b-button size="sm" variant="primary" class="mt-1" @click="loadData">
          {{ $t("Retry") }}
        </b-button>
      </div>

      <template v-else>
        <div class="rci-kpi-grid hf-kpi-grid mb-2">
          <div class="rci-kpi-card rci-kpi-card--accent">
            <div class="rci-kpi-card__icon">
              <feather-icon icon="LayersIcon" size="18" />
            </div>
            <div>
              <div class="rci-kpi-card__label">{{ $t("Held fees") }}</div>
              <div class="rci-kpi-card__value">
                {{ formatCount(stats.totalHoldCount) }}
              </div>
            </div>
          </div>
          <div class="rci-kpi-card rci-kpi-card--warn">
            <div class="rci-kpi-card__icon">
              <feather-icon icon="TrendingUpIcon" size="18" />
            </div>
            <div>
              <div class="rci-kpi-card__label">{{ $t("Total amount") }}</div>
              <div class="rci-kpi-card__value rci-kpi-card__value--sm">
                {{ formatAmount(stats.totalHoldAmount) }}
              </div>
            </div>
          </div>
          <div class="rci-kpi-card rci-kpi-card--good">
            <div class="rci-kpi-card__icon">
              <feather-icon icon="UsersIcon" size="18" />
            </div>
            <div>
              <div class="rci-kpi-card__label">{{ $t("Students") }}</div>
              <div class="rci-kpi-card__value">
                {{ formatCount(stats.totalStudents) }}
              </div>
            </div>
          </div>
        </div>

        <div class="rci-panel rci-panel--table">
          <div class="rci-panel__head rci-panel__head--table">
            <div
              class="d-flex align-items-center justify-content-between w-100 flex-wrap"
            >
              <div class="d-flex align-items-center">
                <div class="rci-panel__badge rci-panel__badge--ink mr-75">
                  <feather-icon icon="GridIcon" size="15" />
                </div>
                <div>
                  <h6 class="rci-panel__title mb-0">{{ $t("Hold records") }}</h6>
                  <small class="rci-panel__sub">{{
                    $t("Students with valid name or GR only")
                  }}</small>
                </div>
              </div>
            </div>
          </div>

          <div v-if="displayRecords.length" class="table-responsive rci-table-scroll">
            <b-table
              :items="displayRecords"
              :fields="tableFields"
              small
              hover
              sticky-header="340px"
              class="hf-table mb-0"
              show-empty
            >
              <template #head()="scope">
                <span class="text-nowrap">{{ $t(scope.label) }}</span>
              </template>
              <template #cell(studentName)="data">
                <button
                  v-if="canOpenChallan"
                  type="button"
                  class="hf-student-link"
                  @click="openStudentChallan(data.item)"
                >
                  <span class="hf-student-link__text">
                    <span class="hf-student-link__name">{{
                      data.item.studentName
                    }}</span>
                    <small
                      v-if="data.item.grNo"
                      class="hf-student-link__gr d-block"
                    >
                      GR {{ data.item.grNo }}
                    </small>
                  </span>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="16"
                    class="hf-student-link__chev"
                  />
                </button>
                <div v-else class="hf-student-static">
                  <span class="font-weight-bold">{{
                    data.item.studentName
                  }}</span>
                  <small
                    v-if="data.item.grNo"
                    class="text-muted d-block"
                  >
                    GR {{ data.item.grNo }}
                  </small>
                </div>
              </template>
              <template #cell(feeTypeName)="data">
                {{ data.item.feeTypeName || "—" }}
              </template>
              <template #cell(monthYear)="data">
                <span class="rci-chip">{{ data.item.monthYear || "—" }}</span>
              </template>
              <template #cell(amount)="data">
                <span class="hf-amount">{{ formatAmount(data.item.amount) }}</span>
              </template>
              <template #cell(date)="data">
                <span class="rci-chip rci-chip--soft">{{
                  formatDate(data.item.date)
                }}</span>
              </template>
              <template #cell(heldOn)="data">
                <span class="text-muted small">{{
                  formatDateTime(data.item.heldOn)
                }}</span>
              </template>
              <template #cell(reason)="data">
                <button
                  v-if="reasonText(data.item.reason)"
                  type="button"
                  class="hf-reason-btn"
                  @click.stop="openReasonModal(data.item)"
                >
                  {{ truncateReason(data.item.reason) }}
                </button>
                <span v-else class="text-muted">—</span>
              </template>
            </b-table>
          </div>

          <div v-else class="rci-state py-4">
            <div class="rci-state__icon">
              <feather-icon icon="CheckCircleIcon" size="28" />
            </div>
            <h5 class="rci-state__title">{{ $t("No held fees to show") }}</h5>
            <p class="rci-state__sub mb-0">
              {{ $t("No held fees in this period.") }}
            </p>
          </div>
        </div>
      </template>
    </b-card-body>

    <b-modal
      v-model="reasonModalVisible"
      :title="$t('Hold reason')"
      centered
      ok-only
      ok-variant="primary"
      :ok-title="$t('Close')"
      size="md"
    >
      <p v-if="reasonModalStudent" class="text-muted small mb-50">
        {{ reasonModalStudent }}
      </p>
      <p v-if="reasonModalHoldTill" class="text-muted small mb-1">
        {{ $t("Hold till") }}:
        <strong>{{ formatDate(reasonModalHoldTill) }}</strong>
      </p>
      <div class="hf-reason-modal-body">
        {{ reasonModalText || "—" }}
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import flatPickr from "vue-flatpickr-component";
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BTable,
  BDropdown,
  BDropdownItem,
} from "bootstrap-vue";
import { VBTooltip } from "bootstrap-vue";
import { mapActions } from "vuex";

export default {
  directives: {
    "b-tooltip": VBTooltip,
  },
  components: {
    flatPickr,
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BTable,
    BDropdown,
    BDropdownItem,
  },
  props: {
    campusID: {
      type: [Number, String],
      default: null,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      loadError: false,
      hasLoadedOnce: false,
      loadSeq: 0,
      dtFrom: "",
      dtTo: "",
      datePeriod: "all",
      periodOptions: [
        { value: "all", label: "All time" },
        { value: "7d", label: "Last 7 days" },
        { value: "30d", label: "Last 30 days" },
        { value: "month", label: "This month" },
        { value: "custom", label: "Custom range" },
      ],
      stats: {
        totalHoldCount: 0,
        totalHoldAmount: 0,
        totalStudents: 0,
        campusID: null,
        allCampuses: false,
      },
      records: [],
      reasonModalVisible: false,
      reasonModalText: "",
      reasonModalStudent: "",
      reasonModalHoldTill: null,
      datePickrConfig: {
        dateFormat: "Y-m-d",
        allowInput: true,
        disableMobile: true,
      },
      tableFields: [
        { key: "studentName", label: "student" },
        { key: "feeTypeName", label: "fee type" },
        { key: "monthYear", label: "period" },
        { key: "amount", label: "amount" },
        { key: "date", label: "hold till" },
        { key: "heldOn", label: "held on" },
        { key: "reason", label: "reason" },
      ],
      _skipPeriodWatch: false,
    };
  },
  computed: {
    periodLabel() {
      const opt = this.periodOptions.find((o) => o.value === this.datePeriod);
      return opt ? this.$t(opt.label) : this.$t("All time");
    },
    canOpenChallan() {
      const rights = this.$store.state.rights || {};
      const ch = rights["manage-challan"];
      return !!(ch && ch.view);
    },
    displayRecords() {
      return (this.records || []).filter((row) => this.rowHasStudent(row));
    },
  },
  watch: {
    campusID() {
      this.loadData();
    },
    dtFrom() {
      if (this._skipPeriodWatch) return;
      if (this.datePeriod !== "custom") this.datePeriod = "custom";
      this.scheduleLoad();
    },
    dtTo() {
      if (this._skipPeriodWatch) return;
      if (this.datePeriod !== "custom") this.datePeriod = "custom";
      this.scheduleLoad();
    },
  },
  created() {
    this.loadData();
  },
  beforeDestroy() {
    if (this._loadTimer) clearTimeout(this._loadTimer);
  },
  methods: {
    ...mapActions({ get: "get" }),

    rowHasStudent(row) {
      if (!row) return false;
      const name = (row.studentName || "").trim();
      if (name && name !== "—" && name !== "-") return true;
      const gr = row.grNo != null ? String(row.grNo).trim() : "";
      if (gr && gr !== "—" && gr !== "-") return true;
      const sid = Number(row.sID);
      return sid > 0;
    },

    openStudentChallan(row) {
      if (!this.canOpenChallan || !this.rowHasStudent(row)) return;
      const query = {};
      const gr = row.grNo != null ? String(row.grNo).trim() : "";
      const sid = Number(row.sID);
      if (gr && gr !== "—" && gr !== "-") query.grNo = gr;
      if (sid > 0) query.sID = String(sid);
      if (!query.grNo && !query.sID) return;
      const route = this.$router.resolve({
        name: "manage-challan",
        query,
      });
      window.open(route.href, "_blank", "noopener,noreferrer");
    },

    openPicker(which) {
      const ref =
        which === "from" ? this.$refs.pickerFrom : this.$refs.pickerTo;
      const fp = ref && ref.fp;
      if (fp && typeof fp.open === "function") fp.open();
    },

    formatYmd(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },

    applyPeriod(value) {
      this.datePeriod = value;
      this._skipPeriodWatch = true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (value === "all") {
        this.dtFrom = "";
        this.dtTo = "";
      } else if (value === "7d") {
        const from = new Date(today);
        from.setDate(from.getDate() - 6);
        this.dtFrom = this.formatYmd(from);
        this.dtTo = this.formatYmd(today);
      } else if (value === "30d") {
        const from = new Date(today);
        from.setDate(from.getDate() - 29);
        this.dtFrom = this.formatYmd(from);
        this.dtTo = this.formatYmd(today);
      } else if (value === "month") {
        const from = new Date(today.getFullYear(), today.getMonth(), 1);
        this.dtFrom = this.formatYmd(from);
        this.dtTo = this.formatYmd(today);
      }
      this._skipPeriodWatch = false;

      if (value !== "custom") {
        this.loadData();
      }
    },

    clearDates() {
      this.datePeriod = "all";
      this._skipPeriodWatch = true;
      this.dtFrom = "";
      this.dtTo = "";
      this._skipPeriodWatch = false;
      this.loadData();
    },

    scheduleLoad() {
      if (this._loadTimer) clearTimeout(this._loadTimer);
      this._loadTimer = setTimeout(() => this.loadData(), 450);
    },

    resolvedCampusId() {
      if (this.campusID != null && this.campusID !== "") {
        return this.campusID;
      }
      return this.$store.state.userData.cId;
    },

    formatCount(val) {
      const n = Number(val);
      if (Number.isNaN(n)) return "0";
      return n.toLocaleString();
    },

    formatAmount(val) {
      const n = Number(val);
      if (Number.isNaN(n)) return "—";
      return `Rs. ${n.toLocaleString()}`;
    },

    formatDate(val) {
      if (!val) return "—";
      try {
        return new Date(val).toLocaleDateString("en-UK", {
          year: "numeric",
          day: "numeric",
          month: "short",
        });
      } catch (_) {
        return String(val);
      }
    },

    formatDateTime(val) {
      if (!val) return "—";
      try {
        return new Date(val).toLocaleString("en-UK", {
          year: "numeric",
          day: "numeric",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (_) {
        return String(val);
      }
    },

    reasonText(text) {
      if (text == null || text === "") return "";
      return String(text).trim();
    },

    truncateReason(text, maxLen = 36) {
      const t = this.reasonText(text);
      if (!t) return "";
      if (t.length <= maxLen) return t;
      return `${t.slice(0, maxLen)}…`;
    },

    openReasonModal(row) {
      this.reasonModalText = this.reasonText(row.reason);
      const name = row.studentName || "";
      const gr = row.grNo != null && row.grNo !== "" ? `GR ${row.grNo}` : "";
      this.reasonModalStudent = [name, gr].filter(Boolean).join(" · ");
      this.reasonModalHoldTill = row.date || null;
      this.reasonModalVisible = true;
    },

    async loadData() {
      const seq = ++this.loadSeq;
      this.loading = true;
      this.loadError = false;

      try {
        const userData = this.$store.state.userData || {};
        if (!userData.db || !userData.token) {
          this.loadError = true;
          return;
        }

        let url =
          this.$store.state.domain +
          "Fees/HoldFeesStats?db=" +
          userData.db +
          "&cID=" +
          this.resolvedCampusId();

        if (this.dtFrom) url += "&dtFrom=" + encodeURIComponent(this.dtFrom);
        if (this.dtTo) url += "&dtTo=" + encodeURIComponent(this.dtTo);

        const res = await this.get({
          url,
          token: userData.token,
          dashboard: true,
        });

        if (seq !== this.loadSeq) return;

        if (!res || res.status !== "success" || !res.data) {
          this.loadError = true;
          this.records = [];
          return;
        }

        const inner = res.data;
        this.stats = {
          totalHoldCount: inner.stats?.totalHoldCount ?? 0,
          totalHoldAmount: inner.stats?.totalHoldAmount ?? 0,
          totalStudents: inner.stats?.totalStudents ?? 0,
          campusID: inner.stats?.campusID ?? null,
          allCampuses: !!inner.stats?.allCampuses,
        };
        this.records = Array.isArray(inner.records) ? inner.records : [];
        this.hasLoadedOnce = true;
      } catch (e) {
        if (seq !== this.loadSeq) return;
        console.error("HoldFeesStats load failed:", e);
        this.loadError = true;
        this.records = [];
      } finally {
        if (seq === this.loadSeq) {
          this.loading = false;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.rci-card {
  border: 1px solid #e0d8ff;
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 4px 18px rgba(115, 103, 240, 0.06);
}

.rci-card-header {
  padding: 1.2rem 1.35rem 1.05rem;
  border-bottom: 1px solid #f0edff;
  background: linear-gradient(180deg, #fbfaff 0%, #fff 100%);
}

.rci-body {
  padding: 1.15rem 1.35rem 1.4rem;
}

.rci-header-icon {
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(145deg, #f3f0ff, #ebe6ff);
  color: #7367f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.12);

  &__ring {
    position: absolute;
    inset: -3px;
    border-radius: 16px;
    border: 1px solid rgba(115, 103, 240, 0.18);
    pointer-events: none;
  }
}

.rci-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2d1b69;
}

.rci-subtitle {
  display: inline-flex;
  align-items: center;
  font-size: 0.8125rem;
  color: #6e6b7b;
}

.rci-status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;

  &--demo {
    background: #fff4e6;
    color: #ff9f43;
    border: 1px solid #ffe0b8;
  }

  &--live {
    background: #e8f9f0;
    color: #28c76f;
    border: 1px solid #c8efd9;
  }
}

.rci-header-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  justify-content: flex-end;
}

.rci-meta-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: #faf9ff;
  border: 1px solid #ebe9f1;
  color: #6e6b7b;
  font-size: 0.72rem;
  font-weight: 600;

  &--mode {
    background: #f3f0ff;
    border-color: #e0d8ff;
    color: #7367f0;
  }
}

.rci-filter-bar {
  padding: 1rem 1.05rem 1.05rem;
  background: linear-gradient(180deg, #fbfbfc 0%, #faf9ff 100%);
  border: 1px solid #f0edff;
  border-radius: 14px;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.35rem;
    margin-bottom: 0.85rem;
    padding-bottom: 0.7rem;
    border-bottom: 1px dashed #e8e4f5;
  }

  &__title {
    display: inline-flex;
    align-items: center;
    font-size: 0.82rem;
    font-weight: 700;
    color: #5e5873;
  }

  &__hint {
    color: #b9b9c3;
    font-size: 0.72rem;
  }

  &__grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.85rem;
    align-items: flex-end;
  }
}

.rci-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.28rem 0.55rem;
  border-radius: 999px;
  background: #f3f0ff;
  border: 1px solid #e0d8ff;
  color: #7367f0;
  font-size: 0.72rem;
  font-weight: 700;

  &--soft {
    background: #fff;
    color: #6e6b7b;
    border-color: #ebe9f1;
  }
}

.rci-kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.75rem;
}

.rci-kpi-card {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem 0.95rem;
  border-radius: 14px;
  border: 1px solid #ebe9f1;
  background: #fff;
  box-shadow: 0 2px 8px rgba(45, 27, 105, 0.03);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: #e0d8ff;
  }

  &--good::before {
    background: #28c76f;
  }
  &--warn::before {
    background: #ff9f43;
  }
  &--accent::before {
    background: #7367f0;
  }

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: #f3f0ff;
    color: #7367f0;
  }

  &--good &__icon {
    background: #e8f9f0;
    color: #28c76f;
  }
  &--warn &__icon {
    background: #fff4e6;
    color: #ff9f43;
  }

  &__label {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #b9b9c3;
    margin-bottom: 0.2rem;
  }

  &__value {
    font-size: 1.12rem;
    font-weight: 700;
    color: #5e5873;
    line-height: 1.2;

    &--sm {
      font-size: 0.95rem;
    }
  }
}

.rci-panel {
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(45, 27, 105, 0.03);

  &--table {
    padding: 0;
    overflow: hidden;
  }

  &__head--table {
    padding: 0.95rem 1rem 0.75rem;
    border-bottom: 1px solid #f0edff;
    background: #faf9ff;
  }

  &__badge {
    width: 36px;
    height: 36px;
    border-radius: 11px;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &--ink {
      background: #f3f0ff;
      color: #7367f0;
    }
  }

  &__title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #2d1b69;
  }

  &__sub {
    font-size: 0.78rem;
    color: #6e6b7b;
  }
}

.rci-table-scroll {
  overflow-x: auto;
}

.hf-panel--embedded {
  border-radius: 18px !important;
  border: 1px solid #e8ecf4 !important;
  box-shadow: 0 6px 22px rgba(15, 23, 42, 0.05);
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 392px;
  max-height: 392px;

  .rci-card-header {
    padding: 0.75rem 1rem !important;
    flex-shrink: 0;
  }

  .rci-subtitle {
    font-size: 0.72rem;
  }

  .rci-body {
    flex: 1;
    overflow-x: hidden;
    overflow-y: auto;
    min-height: 0;
    max-height: none;
    padding: 0.65rem 1rem 0.85rem !important;
    scrollbar-width: thin;
  }

  .rci-filter-bar {
    margin-bottom: 0.65rem !important;

    &__hint {
      display: none;
    }
  }

  .hf-kpi-grid {
    margin-bottom: 0.65rem !important;
  }

  .rci-kpi-card {
    padding: 0.55rem 0.65rem;
  }

  .rci-table-scroll {
    max-height: 140px;
    overflow: auto;
  }
}

.rci-state {
  text-align: center;
  padding: 2rem 1.25rem;
  border-radius: 16px;
  border: 1px dashed #e0d8ff;
  background: linear-gradient(180deg, #fbfaff 0%, #f7f5ff 100%);

  &__icon {
    width: 56px;
    height: 56px;
    margin: 0 auto 0.75rem;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border: 1px solid #e0d8ff;
    color: #7367f0;
  }

  &__title {
    font-size: 1rem;
    font-weight: 700;
    color: #5e5873;
  }

  &__sub {
    font-size: 0.84rem;
    color: #6e6b7b;
  }

  &--error {
    border-color: #f5c6c6;
    background: linear-gradient(180deg, #fffafa 0%, #fff5f5 100%);

    .rci-state__icon {
      color: #ea5455;
      border-color: #f5c6c6;
    }
  }
}

.rci-skeleton {
  min-height: 120px;
}

.rci-skel-block {
  border-radius: 12px;
  background: linear-gradient(90deg, #f3f0ff, #faf9ff, #f3f0ff);
  background-size: 200% 100%;
  animation: hfRciPulse 1.3s ease-in-out infinite;
}

@keyframes hfRciPulse {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

.hf-kpi-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

@media (max-width: 991px) {
  .hf-kpi-grid {
    grid-template-columns: 1fr;
  }
}

.hf-filter-actions {
  align-items: stretch;
  width: 100%;
}

.hf-period-dd {
  ::v-deep .hf-period-dd__toggle {
    display: inline-flex;
    align-items: center;
    font-weight: 600;
    border-radius: 10px;
    white-space: nowrap;
  }
}

.hf-date-control {
  display: inline-flex;
  align-items: stretch;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  height: 42px;
  background: #fff;
  border: 1px solid #e0d8ff;
  border-radius: 11px;
  box-shadow: 0 1px 4px rgba(115, 103, 240, 0.06);
  overflow: hidden;

  &:focus-within {
    border-color: #7367f0;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    flex-shrink: 0;
    color: #7367f0;
    background: #f3f0ff;
    border-right: 1px solid #ebe9f1;
  }

  &__field {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0.15rem 0.65rem;
    min-width: 110px;
    flex: 1;
    cursor: pointer;
  }

  &__label {
    font-size: 0.58rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #b9b9c3;
    line-height: 1;
  }

  &__input {
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
    height: auto !important;
    min-height: 0 !important;
    font-size: 0.82rem !important;
    font-weight: 600;
    color: #5e5873 !important;
    box-shadow: none !important;
    cursor: pointer;
  }

  &__sep {
    display: inline-flex;
    align-items: center;
    color: #b9b9c3;
    font-size: 0.75rem;
    padding: 0 0.1rem;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    flex-shrink: 0;
    border: none;
    background: transparent;
    color: #6e6b7b;
    cursor: pointer;

    &:hover {
      background: #faf9ff;
      color: #7367f0;
    }

    &--primary:hover {
      background: #f3f0ff;
    }
  }

  &__divider {
    width: 1px;
    align-self: stretch;
    background: #ebe9f1;
  }
}

.hf-table {
  ::v-deep thead th {
    background: #f3f0ff !important;
    color: #5e5873 !important;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-color: #f0edff !important;
    white-space: nowrap;
  }

  ::v-deep tbody td {
    vertical-align: middle;
    font-size: 0.84rem;
    border-color: #f0edff !important;
    color: #5e5873;
  }

  ::v-deep tbody tr:hover td {
    background: #faf9ff !important;
  }
}

.hf-student-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  max-width: 220px;
  padding: 0.35rem 0.55rem;
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  background: linear-gradient(120deg, #fbfaff 0%, #fff 100%);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    transform 0.15s ease;

  &:hover {
    border-color: #7367f0;
    box-shadow: 0 4px 14px rgba(115, 103, 240, 0.12);
    transform: translateY(-1px);
  }

  &__name {
    font-weight: 700;
    color: #2d1b69;
    font-size: 0.84rem;
  }

  &__gr {
    color: #6e6b7b;
    font-size: 0.72rem;
  }

  &__chev {
    color: #7367f0;
    flex-shrink: 0;
  }
}

.hf-student-static {
  max-width: 220px;
}

.hf-amount {
  font-weight: 700;
  color: #ea5455;
  white-space: nowrap;
}

.hf-reason-btn {
  display: inline-block;
  max-width: 180px;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  border: 1px solid #e0d8ff;
  background: #f3f0ff;
  color: #7367f0;
  font-size: 0.75rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: #ebe6ff;
  }
}

.hf-reason-modal-body {
  white-space: pre-wrap;
  word-break: break-word;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  border: 1px solid #f0edff;
  background: #faf9ff;
  font-size: 0.92rem;
  line-height: 1.5;
  color: #2f2b3d;
}

@media (max-width: 767px) {
  .hf-date-control {
    flex-wrap: wrap;
    height: auto;
    min-height: 42px;

    &__field {
      min-width: 45%;
    }
  }
}
</style>
